import { ApiError, errorMessage } from '@/shared/api/errors';

/**
 * What the second factor screens say (contract v10.45, ADR 0039 of the back), apart from
 * the components so the tests can read it.
 */

/** What is being typed: the six digits of the app, or one of the ten recovery codes. */
export type CodeKind = 'app' | 'recovery';

/** Digits of a code of the app. */
export const CODE_DIGITS = 6;

/** «123 456», «123-456» or a paste with a newline: what is kept is the digits, up to six. */
export function onlyDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, CODE_DIGITS);
}

export const WRONG_APP_CODE =
  'Ese código no es correcto. Escribe el que sale ahora en la aplicación; si sigue sin entrar, comprueba que la hora del móvil está en automático.';

/** The back's sentence for a wrong code talks about the app, which is not what a person
 * typing a recovery code has in front of them. */
export const WRONG_RECOVERY_CODE =
  'Ese código de recuperación no es correcto o ya se usó. Cada uno sirve una sola vez.';

/** When the ten minutes after the password ran out and the API did not say it itself. */
export const RESTART_MESSAGE = 'Ha pasado demasiado tiempo. Vuelve a escribir tu contraseña.';

/**
 * The pending step after the password is gone (`second_factor_restart`, or the 401 of a
 * setup whose ten minutes ran out): the only way on is the password again. A wrong code is
 * a 401 too, and so is a wrong password typed again, but those are answered on the same
 * screen.
 */
export function needsRestart(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.isUnauthorized &&
    error.reason !== 'wrong_code' &&
    error.reason !== 'wrong_password'
  );
}

/** The password typed again (turning it on from the profile, new recovery codes) was not
 * the right one. The API's sentence says so. */
export function isWrongPassword(error: unknown): boolean {
  return error instanceof ApiError && error.reason === 'wrong_password';
}

/**
 * Back at the password from setting the app up: the next login draws another QR (the one
 * on screen was only good for that login), so an entry already scanned from it will never
 * open the door.
 */
export const SETUP_RESTART_MESSAGE =
  'Ha pasado demasiado tiempo. Vuelve a escribir tu contraseña y escanea el código QR nuevo; si ya habías escaneado el anterior, borra esa entrada de la aplicación.';

/** The sentence for the door when the second factor sends the person back to it. */
export function restartMessage(error: unknown, during: 'verify' | 'setup' = 'verify'): string {
  if (during === 'setup') return SETUP_RESTART_MESSAGE;
  if (error instanceof ApiError && error.reason === 'second_factor_restart') return error.detail;
  return RESTART_MESSAGE;
}

/**
 * A code that did not open: the front's sentence for a wrong one, which knows whether it
 * was the app's or a recovery code; the API's own `detail` for everything else (too many
 * tries, the server cannot check it now).
 */
export function codeErrorMessage(error: unknown, kind: CodeKind): string {
  if (error instanceof ApiError && error.reason === 'wrong_code') {
    return kind === 'recovery' ? WRONG_RECOVERY_CODE : WRONG_APP_CODE;
  }
  return errorMessage(error);
}

/** The text file «Descargar» saves: the codes with what they are for, to print or keep. */
export function recoveryCodesFile(codes: readonly string[], account: string, at: Date): string {
  const day = new Intl.DateTimeFormat('es-ES', { dateStyle: 'long' }).format(at);
  return [
    'Códigos de recuperación de Cubas',
    `Cuenta: ${account}`,
    `Generados el ${day}`,
    '',
    'Cada código sirve una sola vez para entrar sin el móvil.',
    'Guárdalos en papel o en un gestor de contraseñas, no en el mismo móvil.',
    'Al pedir códigos nuevos, estos dejan de valer.',
    '',
    ...codes,
    '',
  ].join('\n');
}
